import { useEffect, useState } from "react";
import { ArrowRight, Share2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";

/**
 * CharterMilestone
 *
 * Dedicated "we are one step closer to NCUA Charter Approval" hero.
 * Uses the official TDSP FCU signage mockup as a cinematic background and
 * renders TWO live progress rings:
 *   - 3,000 signed Charter-Support Pledge Cards
 *   - $500,000 Seed Capital (escrow at Citizen Trust Bank)
 *
 * Reads live counts from /api/credit-union/stats.
 *
 * This is NCUA-compliant: we never say "member", we say "Charter Supporter".
 * We never say "deposit", we say "pledge". Full disclaimer belongs on the
 * host page (CreditUnion.jsx already renders one).
 */

const GOAL_CARDS = 3000;
const GOAL_CAPITAL = 500000;

function AnimatedNumber({ value, prefix = "", suffix = "", duration = 1200 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const from = display;
    const to = Number(value) || 0;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <span>
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>
  );
}

function ProgressRing({ pct, label, size = 220, stroke = 14, children }) {
  const safe = Math.max(0, Math.min(100, pct || 0));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (safe / 100) * c;
  return (
    <div className="flex flex-col items-center" data-testid={`milestone-ring-${label}`}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <defs>
            <linearGradient id={`silver-${label}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#F5F5F5" />
              <stop offset="40%" stopColor="#C0C0C0" />
              <stop offset="100%" stopColor="#7A7A7A" />
            </linearGradient>
            <filter id={`glow-${label}`}>
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke="rgba(192,192,192,0.12)"
            strokeWidth={stroke}
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke={`url(#silver-${label})`}
            strokeWidth={stroke}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c}`}
            filter={`url(#glow-${label})`}
            style={{ transition: "stroke-dasharray 1.2s cubic-bezier(0.4,0,0.2,1)" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function CharterMilestone({ compact = false }) {
  const [stats, setStats] = useState({ count: 0, total_pledged: 0 });

  useEffect(() => {
    const load = () => {
      api
        .get("/credit-union/stats")
        .then((r) => setStats(r.data || {}))
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 25000);
    return () => clearInterval(t);
  }, []);

  const cards = stats.count || 0;
  const capital = stats.total_pledged || 0;
  const cardsPct = Math.min(100, (cards / GOAL_CARDS) * 100);
  const capitalPct = Math.min(100, (capital / GOAL_CAPITAL) * 100);

  // compact (homepage card) variant
  if (compact) {
    return (
      <section
        className="relative overflow-hidden border-y border-[#C0C0C0]/25"
        data-testid="milestone-compact-section"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.72), rgba(0,0,0,0.88)), url('/brand/tdspfcu-signage.jpg')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-16">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div
                className="label-eyebrow"
                style={{ color: "rgba(224,224,224,0.8)" }}
              >
                Charter Milestone · In Progress
              </div>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-bdu-cream leading-tight">
                <span className="bg-clip-text text-transparent" style={{
                  backgroundImage: "linear-gradient(135deg, #FFFFFF 0%, #C0C0C0 50%, #808080 100%)",
                }}>
                  3,000 Signed Pledges · $500,000 Seed Capital
                </span>
                <span className="block mt-2 text-bdu-cream/85 text-lg sm:text-xl font-sans tracking-wide">
                  One step closer to NCUA Charter Approval — transferring the wealth back to the community.
                </span>
              </h2>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/credit-union"
                  className="inline-flex items-center gap-2 px-7 py-3.5 uppercase tracking-widest text-xs font-bold text-black transition-all hover:brightness-110 shadow-[0_14px_40px_-10px_rgba(224,224,224,0.55)]"
                  style={{
                    background:
                      "linear-gradient(135deg, #FFFFFF 0%, #D8D8D8 40%, #B0B0B0 100%)",
                    border: "1px solid #B0B0B0",
                  }}
                  data-testid="milestone-compact-cta"
                >
                  See Live Progress <ArrowRight size={16} />
                </Link>
                <Link
                  to="/credit-union#subscribe"
                  className="btn-ghost"
                  data-testid="milestone-compact-pledge-cta"
                >
                  Sign My Pledge Card
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-black/60 backdrop-blur-sm border border-[#C0C0C0]/30 p-5">
                  <div className="text-[0.6rem] uppercase tracking-widest text-[#C0C0C0]/70">
                    Pledge Cards
                  </div>
                  <div className="mt-1 font-serif text-3xl text-bdu-cream">
                    <AnimatedNumber value={cards} /> <span className="text-[#C0C0C0]/50 text-base">/ 3,000</span>
                  </div>
                  <div className="mt-3 h-1.5 bg-[#C0C0C0]/15 overflow-hidden">
                    <div
                      className="h-full"
                      style={{
                        width: `${cardsPct}%`,
                        background:
                          "linear-gradient(90deg, #F5F5F5 0%, #C0C0C0 100%)",
                        transition: "width 1.2s cubic-bezier(0.4,0,0.2,1)",
                      }}
                    />
                  </div>
                </div>
                <div className="bg-black/60 backdrop-blur-sm border border-[#C0C0C0]/30 p-5">
                  <div className="text-[0.6rem] uppercase tracking-widest text-[#C0C0C0]/70">
                    Seed Capital
                  </div>
                  <div className="mt-1 font-serif text-3xl text-bdu-cream">
                    <AnimatedNumber value={capital} prefix="$" />
                    <span className="text-[#C0C0C0]/50 text-base"> / $500K</span>
                  </div>
                  <div className="mt-3 h-1.5 bg-[#C0C0C0]/15 overflow-hidden">
                    <div
                      className="h-full"
                      style={{
                        width: `${capitalPct}%`,
                        background:
                          "linear-gradient(90deg, #F5F5F5 0%, #C0C0C0 100%)",
                        transition: "width 1.2s cubic-bezier(0.4,0,0.2,1)",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // full hero variant for /credit-union
  const share = async () => {
    const text =
      "We're one step closer to NCUA Charter Approval. 3,000 signed pledge cards + $500,000 seed capital = Proposed Trillion Dollar Spending Power Federal Credit Union. Transferring the wealth back to the community.";
    const url = window.location.origin + "/credit-union";
    try {
      if (navigator.share) {
        await navigator.share({ title: "TDSP FCU Charter Milestone", text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        // eslint-disable-next-line no-alert
        alert("Link copied — share it with your community.");
      }
    } catch (_) {}
  };

  return (
    <section
      className="relative overflow-hidden border-y border-[#C0C0C0]/25"
      data-testid="milestone-hero-section"
    >
      {/* Signage photographic backdrop with dark overlay */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.9) 70%), url('/brand/tdspfcu-signage.jpg')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {/* soft silver glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(1000px 500px at 50% 20%, rgba(224,224,224,0.14) 0%, rgba(0,0,0,0) 70%)",
        }}
      />
      {/* subtle shimmer sweep */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute -inset-y-10 -left-1/3 w-1/3 opacity-30 animate-[shimmer_6s_linear_infinite]"
          style={{
            background:
              "linear-gradient(100deg, transparent 0%, rgba(255,255,255,0.08) 50%, transparent 100%)",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-20 lg:py-28">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 label-eyebrow" style={{ color: "rgba(224,224,224,0.85)" }}>
            <Sparkles size={12} className="text-[#E0E0E0]" />
            Charter Milestone · In Progress
          </div>

          {/* Official logo */}
          <img
            src="/brand/tdspfcu-logo.jpg"
            alt="TDSP FCU — Proposed Trillion Dollar Spending Power Federal Credit Union"
            className="mt-8 mx-auto h-28 sm:h-36 lg:h-40 w-auto object-contain shadow-[0_30px_60px_-20px_rgba(224,224,224,0.4)]"
            data-testid="milestone-tdspfcu-logo"
          />

          <h2
            className="mt-10 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            data-testid="milestone-headline"
          >
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, #FFFFFF 0%, #E0E0E0 30%, #C0C0C0 60%, #808080 100%)",
                filter: "drop-shadow(0 2px 20px rgba(224,224,224,0.25))",
              }}
            >
              One step closer to NCUA Charter Approval.
            </span>
            <span className="block mt-4 text-bdu-cream/90 text-xl sm:text-2xl lg:text-3xl font-sans tracking-wide">
              Transferring the wealth back to the community.
            </span>
          </h2>

          <p className="mt-6 text-bdu-cream/70 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Let potential Charter Supporters know: with{" "}
            <span className="text-[#E0E0E0] font-semibold">3,000 signed pledge cards</span> and a{" "}
            <span className="text-[#E0E0E0] font-semibold">$500,000 seed-capital raise</span>, we complete the NCUA
            organizers' phase and move the proposed Trillion Dollar Spending Power Federal Credit Union
            toward charter approval.
          </p>
        </div>

        {/* Dual progress rings */}
        <div
          className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 justify-items-center"
          data-testid="milestone-rings-grid"
        >
          <ProgressRing pct={cardsPct} label="cards" size={240}>
            <div className="text-[0.55rem] uppercase tracking-[0.2em] text-[#C0C0C0]/80">
              Pledge Cards Signed
            </div>
            <div className="mt-1 font-serif text-4xl text-bdu-cream" data-testid="milestone-cards-value">
              <AnimatedNumber value={cards} />
            </div>
            <div className="text-xs text-[#C0C0C0]/60 mt-0.5">
              of {GOAL_CARDS.toLocaleString()} goal
            </div>
            <div className="mt-2 font-serif text-base text-[#E0E0E0]">
              {cardsPct.toFixed(1)}%
            </div>
          </ProgressRing>

          <ProgressRing pct={capitalPct} label="capital" size={240}>
            <div className="text-[0.55rem] uppercase tracking-[0.2em] text-[#C0C0C0]/80">
              Seed Capital Raised
            </div>
            <div className="mt-1 font-serif text-4xl text-bdu-cream" data-testid="milestone-capital-value">
              <AnimatedNumber value={capital} prefix="$" />
            </div>
            <div className="text-xs text-[#C0C0C0]/60 mt-0.5">
              of ${GOAL_CAPITAL.toLocaleString()} escrow goal
            </div>
            <div className="mt-2 font-serif text-base text-[#E0E0E0]">
              {capitalPct.toFixed(1)}%
            </div>
          </ProgressRing>
        </div>

        {/* CTAs */}
        <div
          className="mt-14 flex flex-wrap items-center justify-center gap-4"
          data-testid="milestone-ctas"
        >
          <a
            href="#subscribe"
            className="inline-flex items-center gap-2 px-8 py-4 uppercase tracking-widest text-xs font-bold text-black transition-all hover:brightness-110 shadow-[0_14px_40px_-10px_rgba(224,224,224,0.55)]"
            style={{
              background:
                "linear-gradient(135deg, #FFFFFF 0%, #D8D8D8 40%, #B0B0B0 100%)",
              border: "1px solid #B0B0B0",
            }}
            data-testid="milestone-pledge-cta"
          >
            Sign My Pledge Card <ArrowRight size={18} />
          </a>
          <button
            type="button"
            onClick={share}
            className="inline-flex items-center gap-2 px-8 py-4 uppercase tracking-widest text-xs font-bold text-[#E0E0E0] border border-[#C0C0C0]/60 hover:bg-[#C0C0C0]/10 transition"
            data-testid="milestone-share-cta"
          >
            <Share2 size={16} /> Share The Milestone
          </button>
        </div>

        <p className="mt-10 text-center text-[0.65rem] uppercase tracking-widest text-bdu-cream/40 max-w-3xl mx-auto leading-relaxed">
          Proposed · Pending NCUA Charter Approval. All charter-support pledges are held in a segregated
          escrow at Citizen Trust Bank and are not deposits. Full refund if the charter is not granted.
        </p>
      </div>

      {/* Keyframe for shimmer sweep */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(0); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </section>
  );
}
